import { cn } from '@/shared/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import * as React from 'react';

/**
 * Figma 라이브러리 Button (486:4910) — Size × Style × State × Content
 *
 * variant (Style)
 * - primary: Filled/default — 검정 채움
 * - grey: Filled/grey — 회색 채움
 * - outline: Outline — 흰 배경 + 1.5px 회색 테두리 (열린 드롭다운은 테두리 grey-500)
 * - text: Text/black — 배경 없음, 검정 글자
 * - textPrimary: Text/primary — 배경 없음, 파란 글자
 * - error: Filled/error · success: Filled/success
 *
 * size: giant 56 · large 48 · medium 40 · small 32 · tiny 24
 * 아이콘만 있는 버튼(children 없이 leftIcon만)은 정사각형이 된다 (Content=Only Icons).
 * 아이콘은 <Icon name="…-24" />처럼 size에 맞는 아이콘을 넘긴다 (giant · large · medium 24, small 16, tiny 14).
 *
 * 레거시(2단계 화면 이전 전까지 유지): variant secondary → grey, ghost → text, navy, link /
 * size sm → small, md → medium, lg → large, xl → giant, icon → medium 정사각형
 */
const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center whitespace-nowrap transition-colors outline-none select-none disabled:pointer-events-none aria-disabled:pointer-events-none [&>svg]:size-4 [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-grey-900 text-fg-white hover:bg-grey-700 focus-visible:ring-3 focus-visible:ring-brand-200 active:bg-grey-800 disabled:bg-grey-200 disabled:text-fg-disabled',
        grey: 'bg-grey-100 text-fg-secondary hover:bg-grey-200 focus-visible:ring-3 focus-visible:ring-brand-100 active:bg-grey-300 disabled:bg-grey-50 disabled:text-fg-disabled',
        outline:
          'border-[1.5px] border-grey-100 bg-surface-white text-fg-secondary hover:bg-grey-100 focus-visible:ring-3 focus-visible:ring-brand-100 active:bg-grey-200 disabled:bg-surface-white disabled:text-fg-disabled data-[state=open]:border-grey-500',
        text: 'text-fg-primary hover:bg-grey-100 focus-visible:ring-3 focus-visible:ring-brand-100 active:bg-grey-200 disabled:bg-transparent disabled:text-fg-disabled',
        textPrimary:
          'text-fg-accent hover:bg-brand-75 hover:text-brand-800 focus-visible:ring-3 focus-visible:ring-brand-100 active:bg-brand-100 active:text-fg-accent disabled:bg-transparent disabled:text-brand-300',
        error:
          'bg-red-500 text-fg-white hover:bg-red-400 focus-visible:ring-3 focus-visible:ring-brand-200 active:bg-red-600 disabled:bg-grey-200 disabled:text-fg-disabled',
        success:
          'bg-green-500 text-fg-white hover:bg-green-400 focus-visible:ring-3 focus-visible:ring-brand-200 active:bg-green-600 disabled:bg-grey-200 disabled:text-fg-disabled',
        // ── 레거시 ──
        secondary:
          'bg-grey-100 text-fg-secondary hover:bg-grey-200 active:bg-grey-300 disabled:bg-grey-50 disabled:text-fg-disabled',
        navy: 'bg-navy text-white hover:bg-navy/90 disabled:opacity-50',
        ghost:
          'text-fg-primary hover:bg-grey-100 active:bg-grey-200 disabled:text-fg-disabled',
        link: 'h-auto px-0 text-primary hover:underline',
      },
      size: {
        giant: 'h-14 gap-0 rounded-20 px-5 py-4 text-button-18-sb',
        large: 'h-12 gap-0 rounded-16 px-4 py-3.5 text-button-16-sb',
        medium: 'h-10 gap-0 rounded-12 p-3 text-button-14-sb',
        small: 'h-8 gap-0 rounded-12 p-2 text-button-12-sb',
        tiny: 'h-6 gap-0 rounded-8 px-1 py-1.5 text-button-10-sb',
        // ── 레거시 ──
        sm: 'h-8 gap-1 rounded-12 px-3 text-button-12-sb',
        md: 'h-10 gap-1 rounded-12 px-4 text-button-14-sb',
        lg: 'h-12 gap-1 rounded-16 px-5 text-button-16-sb',
        xl: 'h-14 gap-1 rounded-20 px-6 text-button-18-sb',
        icon: 'size-10 rounded-12',
      },
      iconOnly: { true: 'px-0 py-0', false: '' },
    },
    compoundVariants: [
      // Text 스타일은 좌우 여백이 좁다
      {
        variant: ['text', 'textPrimary'],
        size: ['giant', 'large'],
        className: 'px-2',
      },
      { variant: ['text', 'textPrimary'], size: 'medium', className: 'px-1' },
      { variant: ['text', 'textPrimary'], size: 'small', className: 'px-1' },
      { variant: ['text', 'textPrimary'], size: 'tiny', className: 'p-1' },
      // Only Icons — 정사각형
      { iconOnly: true, size: 'giant', className: 'w-14' },
      { iconOnly: true, size: 'large', className: 'w-12' },
      { iconOnly: true, size: 'medium', className: 'w-10' },
      { iconOnly: true, size: 'small', className: 'w-8' },
      { iconOnly: true, size: 'tiny', className: 'w-6' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
      iconOnly: false,
    },
  },
);

type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

/** 글자 묶음의 좌우 여백 — Figma Text 프레임 (tiny만 2px) */
const textPadding = (size: ButtonSize | null | undefined) =>
  size === 'tiny' ? 'px-0.5' : 'px-1';

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof buttonVariants>, 'iconOnly'> {
  asChild?: boolean;
  loading?: boolean;
  /** 글자 앞 아이콘 (<Icon />) */
  leftIcon?: React.ReactNode;
  /** 글자 뒤 아이콘 (<Icon />) */
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const hasText = children !== undefined && children !== null;
    const iconOnly = !asChild && !hasText && !!(leftIcon || rightIcon);
    // 레거시 size는 예전처럼 children을 그대로 둔다 (아이콘 · 글자 간격은 gap으로)
    const legacySize =
      size === 'sm' ||
      size === 'md' ||
      size === 'lg' ||
      size === 'xl' ||
      size === 'icon';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, iconOnly }), className)}
        disabled={disabled || loading}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {loading ? (
              <Loader2 className="animate-spin" aria-hidden />
            ) : (
              leftIcon
            )}
            {hasText &&
              (legacySize ? (
                children
              ) : (
                <span
                  className={cn(
                    // 레거시 호출부가 children에 lucide 아이콘을 함께 넣는 경우를 위해 gap · svg 크기를 둔다
                    'inline-flex items-center justify-center gap-1 [&>svg]:size-4 [&>svg]:shrink-0',
                    textPadding(size ?? 'medium'),
                  )}
                >
                  {children}
                </span>
              ))}
            {rightIcon}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
